import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiShield,FiCheckCircle,FiAlertTriangle,FiUsers,FiHeart,FiDollarSign}=FiIcons;

const WorkplaceViolence=()=> {
  const coverageAreas=[ 
    'Business Interruption','Crisis Management Expert','Public Relations Consultant','Mental Health Counselling','Site Security Services','Bio-Hazard Clean-up Expenses','Burial Expenses','Reward Expenses','Temporary Expenses: Location & Employees Travel Expenses' 
  ];

  const statistics=[ 
    {stat: '1 in 5',description: 'occupational fatalities is attributed to workplace violence'},{stat: '$250B - $350B',description: 'average loss to all US businesses each year from workplace violence'} 
  ];

  const riskFactors=[ 
    'Workers who exchange money with the public','Deliver passengers,goods,or services','Work alone or in small groups','Work during late night or early morning hours','Work in high-crime areas','Work in community settings and homes with extensive public contact' 
  ];

  return ( 
    <div className="min-h-screen"> 
      {/* Hero Section */} 
      <section className="relative bg-gray-900 text-white overflow-hidden"> 
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" 
          style={{backgroundImage: 'url(https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80)',}} 
        /> 
        <div className="absolute inset-0 bg-red-900 bg-opacity-80" /> 
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24"> 
          <div className="text-center"> 
            <motion.h1 
              initial={{opacity: 0,y: 30}} 
              animate={{opacity: 1,y: 0}} 
              transition={{duration: 0.8}} 
              className="text-4xl md:text-5xl font-bold mb-6" 
            > 
              Workplace Violence Insurance Coverage 
            </motion.h1> 
            <motion.p 
              initial={{opacity: 0,y: 30}} 
              animate={{opacity: 1,y: 0}} 
              transition={{duration: 0.8,delay: 0.2}} 
              className="text-xl text-red-100 mb-8 max-w-3xl mx-auto" 
            > 
              Workplace Violence insurance coverage helps small businesses recover from a violent event. Covering a broad range of incidents,this coverage goes beyond typical General Liability and Workers' Compensation coverage. 
            </motion.p> 
            <motion.div 
              initial={{opacity: 0,y: 30}} 
              animate={{opacity: 1,y: 0}} 
              transition={{duration: 0.8,delay: 0.4}} 
              className="flex justify-center" 
            > 
              <a href="https://coterie.com/quote" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors shadow-xl" 
              > 
                Get My Price 
              </a> 
            </motion.div> 
          </div> 
        </div> 
      </section> 

      {/* Statistics Section */} 
      <section className="py-20 bg-red-50"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="text-center mb-12"> 
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"> The Reality of Workplace Violence </h2> 
            <p className="text-xl text-gray-600 max-w-3xl mx-auto"> Workplace violence can strike anywhere,and no one is immune. </p> 
          </div> 
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8"> 
            {statistics.map((item,index)=> ( 
              <motion.div 
                key={index} 
                initial={{opacity: 0,y: 30}} 
                animate={{opacity: 1,y: 0}} 
                transition={{duration: 0.6,delay: index * 0.1}} 
                className="bg-white p-8 rounded-xl shadow-lg text-center" 
              > 
                <div className="text-4xl font-bold text-red-600 mb-4">{item.stat}</div> 
                <p className="text-gray-700 text-lg">{item.description}</p> 
              </motion.div> 
            ))} 
          </div> 
        </div> 
      </section> 

      {/* Coverage Areas Section */} 
      <section className="py-20 bg-gray-50"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="text-center mb-16"> 
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"> Comprehensive Workplace Violence Coverage </h2> 
            <p className="text-xl text-gray-600 max-w-3xl mx-auto"> Coterie's Workplace Violence coverage,in partnership with Berkley Re Solutions,offers small businesses needed help with education and risk management when an event happens. </p> 
          </div> 
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"> 
            {coverageAreas.map((area,index)=> ( 
              <motion.div 
                key={index} 
                initial={{opacity: 0,y: 30}} 
                animate={{opacity: 1,y: 0}} 
                transition={{duration: 0.6,delay: index * 0.1}} 
                className="bg-white p-6 rounded-xl shadow-lg flex items-center space-x-4" 
              > 
                <SafeIcon icon={FiCheckCircle} className="h-6 w-6 text-red-500 flex-shrink-0" /> 
                <span className="text-gray-800 font-medium">{area}</span> 
              </motion.div> 
            ))} 
          </div> 
        </div> 
      </section> 

      {/* Risk Factors Section */} 
      <section className="py-20 bg-white"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="text-center mb-16"> 
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"> Workers at Increased Risk </h2> 
            <p className="text-xl text-gray-600 max-w-3xl mx-auto"> Certain workers face higher risks of workplace violence. Understanding these risk factors helps determine if your business needs specialized coverage. </p> 
          </div> 
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto"> 
            {riskFactors.map((factor,index)=> ( 
              <motion.div 
                key={index} 
                initial={{opacity: 0,x: -30}} 
                animate={{opacity: 1,x: 0}} 
                transition={{duration: 0.6,delay: index * 0.1}} 
                className="bg-gray-50 p-6 rounded-xl flex items-center space-x-4" 
              > 
                <SafeIcon icon={FiAlertTriangle} className="h-6 w-6 text-orange-500 flex-shrink-0" /> 
                <span className="text-gray-800 font-medium">{factor}</span> 
              </motion.div> 
            ))} 
          </div> 
        </div> 
      </section> 

      {/* Why You Need It Section */} 
      <section className="py-20 bg-red-50"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"> 
            <motion.div 
              initial={{opacity: 0,x: -30}} 
              animate={{opacity: 1,x: 0}} 
              transition={{duration: 0.8}} 
            > 
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6"> Why Workplace Violence Coverage Matters </h2> 
              <div className="space-y-6"> 
                <div className="flex items-start space-x-4"> 
                  <SafeIcon icon={FiShield} className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" /> 
                  <div> 
                    <h3 className="font-semibold text-gray-900 mb-2">Beyond Standard Coverage</h3> 
                    <p className="text-gray-600">Goes beyond typical General Liability and Workers' Compensation to address the unique challenges of workplace violence incidents.</p> 
                  </div> 
                </div> 
                <div className="flex items-start space-x-4"> 
                  <SafeIcon icon={FiUsers} className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" /> 
                  <div> 
                    <h3 className="font-semibold text-gray-900 mb-2">Expert Crisis Management</h3> 
                    <p className="text-gray-600">Access to crisis management experts and public relations consultants to help navigate the aftermath of an incident.</p> 
                  </div> 
                </div> 
                <div className="flex items-start space-x-4"> 
                  <SafeIcon icon={FiHeart} className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" /> 
                  <div> 
                    <h3 className="font-semibold text-gray-900 mb-2">Employee Support</h3> 
                    <p className="text-gray-600">Mental health counseling and support services for employees affected by workplace violence incidents.</p> 
                  </div> 
                </div> 
                <div className="flex items-start space-x-4"> 
                  <SafeIcon icon={FiDollarSign} className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" /> 
                  <div> 
                    <h3 className="font-semibold text-gray-900 mb-2">Financial Protection</h3> 
                    <p className="text-gray-600">Coverage for business interruption,cleanup expenses,and other costs associated with violent incidents.</p> 
                  </div> 
                </div> 
              </div> 
            </motion.div> 
            <motion.div 
              initial={{opacity: 0,x: 30}} 
              animate={{opacity: 1,x: 0}} 
              transition={{duration: 0.8,delay: 0.2}} 
              className="bg-white p-8 rounded-xl shadow-xl" 
            > 
              <div className="text-center"> 
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Don't Wait for the Unthinkable</h3> 
                <p className="text-gray-600 mb-6"> Workplace Violence coverage provides peace of mind if the unimaginable occurs. The coverage also provides education including tools,tips,and training. </p> 
                <a href="https://coterie.com/quote" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="block w-full bg-red-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-red-700 transition-colors shadow-lg mb-4" 
                > 
                  Get My Price 
                </a> 
                <p className="text-sm text-gray-500"> Expert guidance available • Education and risk management included </p> 
              </div> 
            </motion.div> 
          </div> 
        </div> 
      </section> 

      {/* CTA Section */} 
      <section className="py-20 bg-red-900 text-white"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"> 
          <h2 className="text-3xl md:text-4xl font-bold mb-6"> Protect Your Business and Employees </h2> 
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto"> Don't wait for the unthinkable to happen to have coverage in place. Get comprehensive workplace violence protection through Burbank Insurance Services and Coterie Insurance. </p> 
          <div className="flex justify-center"> 
            <a href="https://coterie.com/quote" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors shadow-xl" 
            > 
              Get My Price 
            </a> 
          </div> 
          <p className="text-red-200 text-sm mt-4"> CA License #6013543 • Education and risk management included </p> 
        </div> 
      </section> 
    </div> 
  );
};

export default WorkplaceViolence;